// src/home/homeCards.jsx - the market half of Home, ONCE.
//
// WHY THIS FILE EXISTS. These cards render on two pages that answer different
// questions: the agent Home (market briefing only, safe on a projector) and the
// customer portal home (their coverage, their basis, their weather - plus this).
// Left inline in SilasHomeView and copied into PortalHomeView they would be two
// definitions of one rule, and this repo's own history says what happens then:
// three news handlers drifted twice, two guarantee engines disagreed about the
// same farm's forward-sell ceiling, and three copies of normFarmerName have to
// be kept in step by hand. One definition, two callers - the copRules /
// grainTargets / miYieldModel pattern.
//
// !! NOTHING IN HERE MAY READ FARMER DATA. Every component below takes the
// api/market?fn=home payload and nothing else - no farmers prop, no policy prop,
// no Supabase call. That is the same contract MARKET_TABS views hold, and it is
// what makes these safe to render in the portal at all. The payload asserts it
// on the wire as contract.readsFarmerData; tools/check-home-safe.js asserts it
// here.

import React, { useState, useEffect } from "react";

// -- palette (matches the app; no CSS file, inline styles are the system) --
export const INK = "#1a2e1a", MID = "#2d4a2d", MUTED = "#60735f";
export const LINE = "#dfe8df", CARD = "#ffffff";
export const UP = "#2f7d32", DOWN = "#b3261e";

export function homeTheme(isMobile) {
  return {
    card: { background: CARD, border: "1px solid " + LINE, borderRadius: 18, padding: isMobile ? 16 : 22,
            boxShadow: "0 1px 2px rgba(18,36,26,0.04)" },
    h2: { fontFamily: "'Lora',serif", color: INK, fontSize: isMobile ? 19 : 22, margin: "0 0 12px", fontWeight: 600 },
    eyebrow: { fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase", color: MUTED, fontWeight: 800, margin: "0 0 6px" },
  };
}

// ── A 30-SESSION PRICE LINE ───────────────────────────────────────────────
// Drawn from markets.board[].spark, which the API takes from the SAME contract
// the tile prints. Colour follows the direction over the window it draws —
// the window is printed under it, so the colour can be checked.
export function Sparkline({ points, height = 46 }) {
  if (!Array.isArray(points) || points.length < 2) return null;
  const vals = points.map((p) => Number(p.p)).filter((v) => Number.isFinite(v));
  if (vals.length < 2) return null;
  const lo = Math.min(...vals), hi = Math.max(...vals);
  const W = 200, H = height, pad = 3;
  const xy = vals.map((v, i) => [(i / (vals.length - 1)) * W, pad + (1 - (v - lo) / ((hi - lo) || 1)) * (H - 2 * pad)]);
  const line = "M" + xy.map(([x, y]) => x.toFixed(1) + " " + y.toFixed(1)).join(" L");
  const up = vals[vals.length - 1] >= vals[0];
  return (
    <svg viewBox={"0 0 " + W + " " + H} preserveAspectRatio="none" width="100%" height={H}
         aria-hidden="true" style={{ display: "block" }}>
      <path d={line + " L" + W + " " + H + " L0 " + H + " Z"} fill={up ? "rgba(47,125,50,0.10)" : "rgba(179,38,30,0.08)"} />
      <path d={line} fill="none" stroke={up ? UP : DOWN} strokeWidth="2" vectorEffect="non-scaling-stroke"
            strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// ── ONE SENTENCE ABOUT THE CLOSE, FROM THE TILES' OWN NUMBERS ──────────────
// Direction words only — "settled higher / lower" — each one checkable against
// the tile below it. No cause, no verdict, no forecast.
const BOARD_NAMES = { corn: "corn", soybeans: "soybeans", wheat: "wheat",
                      live_cattle: "live cattle", feeder_cattle: "feeder cattle" };
function joinNames(a) {
  if (a.length <= 1) return a.join("");
  return a.slice(0, -1).join(", ") + " and " + a[a.length - 1];
}
export function boardSummaryLine(data) {
  const board = (data && data.markets && data.markets.board) || [];
  const asOf = data && data.markets && data.markets.asOf;
  const up = [], down = [], flat = [];
  for (const m of board) {
    if (m.stale) continue;                       // a stale close is not "today"
    const c = m.nearby;
    if (!c || c.change == null) continue;
    const nm = BOARD_NAMES[m.key] || String(m.label || "").toLowerCase();
    (c.change > 0 ? up : c.change < 0 ? down : flat).push(nm);
  }
  if (!up.length && !down.length) return "";
  const day = asOf ? new Date(String(asOf).slice(0, 10) + "T12:00:00Z")
    .toLocaleDateString("en-US", { weekday: "long", timeZone: "UTC" }) : "";
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  const parts = [];
  if (down.length) parts.push(joinNames(down) + " settled lower");
  if (up.length) parts.push(joinNames(up) + " settled higher");
  return cap(parts.join("; ")) + (day ? " on " + day : "") + ".";
}

export function fmtDate(d) {
  if (!d) return "—";
  const s = String(d).slice(0, 10);
  const t = new Date(s + "T12:00:00Z");
  return isNaN(t) ? s : t.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// "August 25" - the calendar date the perspective chart samples in each year.
export function monthDayLabelOf(now) {
  return (now || new Date()).toLocaleDateString("en-US", { month: "long", day: "numeric" });
}

// -- ONE FETCH, BOTH PAGES ---------------------------------------------------
// `portal` is passed to the API, not applied here: pipeline health and runtime
// absences are stripped SERVER-SIDE so the payload never carries them to a
// customer's browser. A card added later cannot expose what was never sent.
// `states` scopes the REGIONAL facts to ground the reader actually farms.
//
// ⚠ A MARKET REACHES EVERYONE; LOCAL WEATHER DOES NOT. Kevin's 2026-08-25 call
// stands — the briefing is the BIG PICTURE and is deliberately NOT narrowed by what
// a customer insures, because a board price moves their inputs and their neighbours
// whether or not they carry that coverage. Drought severity in sixteen Western New
// York counties is not that. It is somebody else's back yard, and it reached Chris
// Dawson — who farms Michigan, Ohio and Indiana — as "NY drought severity".
// Kevin, 2026-09-07: "A customer in Michigan should not being seeing NY drought
// info…"
//
// The CLIENT declares its states; the endpoint never looks up who is asking. That
// keeps Home's contract intact (it reads no farmer data and check-home-safe still
// asserts it) while letting the payload be honest about locality.
export function useHomeData({ months = {}, portal = false, states = null } = {}) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  // Object identity changes every render; the KEY is what the effect may depend
  // on, or the fetch re-fires forever (the unstable-dep refetch loop this repo
  // has already paid for once).
  const monthsKey = Object.keys(months).sort().map((k) => k + "=" + months[k]).join("&");
  // Same reasoning, same shape: a SORTED, de-duplicated string, computed OUTSIDE the
  // effect so it can legitimately be a dependency. Computed inside it, the array
  // identity would change every render, the dep would be stale, and the fetch would
  // either loop or never refire — the unstable-dep trap this repo has paid for once.
  const stateKey = (Array.isArray(states) && states.length)
    ? [...new Set(states.map((x) => String(x || "").toUpperCase()).filter(Boolean))].sort().join(",")
    : "";

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const qs = [monthsKey, portal ? "portal=1" : "", stateKey ? "states=" + stateKey : ""]
      .filter(Boolean).join("&");
    fetch("/api/market?fn=home" + (qs ? "&" + qs : ""))
      .then((r) => r.json())
      .then((jj) => {
        if (!alive) return;
        if (!jj || jj.ok === false) setErr((jj && jj.error) || "Home data unavailable");
        setData(jj || null);
        setLoading(false);
      })
      .catch((e) => { if (alive) { setErr(String((e && e.message) || e)); setLoading(false); } });
    return () => { alive = false; };
  }, [monthsKey, portal, stateKey]);

  return { data, err, loading };
}
// ── market tiles ───────────────────────────────────────────────────────────
const NAV_FOR = { corn: "grain", soybeans: "grain", wheat: "grain",
                  live_cattle: "markets", feeder_cattle: "markets" };

function Tile({ m, persp = {}, onNav = null, isMobile = false }) {
  const nav = typeof onNav === "function" ? onNav : null;
  const c = m.nearby;
  const isBu = m.unit === "$/bu";
  const chg = c && c.change;
  const chgTxt = chg == null ? null : (isBu ? (chg >= 0 ? "+" : "−") + Math.abs(Math.round(chg * 100)) + "¢"
                                            : (chg >= 0 ? "+" : "−") + "$" + Math.abs(chg).toFixed(2));
  const p = persp[m.key];
  return (
    <button
      onClick={nav ? () => nav(NAV_FOR[m.key] || "markets") : undefined}
      style={{ textAlign: "left", background: CARD, border: "1px solid " + LINE, borderRadius: 18,
               padding: isMobile ? "14px 14px 10px" : "16px 18px 12px", cursor: nav ? "pointer" : "default",
               width: "100%", display: "block", fontFamily: "inherit", color: INK,
               boxShadow: "0 6px 18px rgba(18,36,26,0.07)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <span style={{ color: INK, fontWeight: 800, fontSize: 14 }}>{m.label}</span>
        <span style={{ color: MUTED, fontSize: 12 }}>{c ? c.month : m.unit}</span>
      </div>
      {/* ⚠ A STALE TILE STAYS ON THE BOARD AND SAYS SO. Its commodity did not
          settle on the board's date, so the price below is the last one it did
          settle at. The tile used to be dropped entirely and the reason filed in
          a collapsed absence list, which is how a limit-up cattle day showed up
          as nothing at all. Both dates are printed: a stale tile can never be
          read as a current one. */}
      {m.stale ? (
        <div style={{ color: "#a5461b", fontSize: 10, fontWeight: 700, marginTop: 2 }}>
          No settle for {fmtDate(m.stale.boardDate)} — showing {fmtDate(m.stale.settleDate)}
        </div>
      ) : null}
      {/* The board month rolls on its own (api/_futures-rollover.js). When the
          next contract has no settle yet the tile keeps the current one and says so. */}
      {m.rollHeld ? (
        <div style={{ color: MUTED, fontSize: 10, fontWeight: 700, marginTop: 2 }}>
          Next contract has no settle yet — still showing {m.rollHeld.showing}
        </div>
      ) : null}
      {c ? (
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
          <span style={{ color: INK, fontFamily: "'Lora',serif", fontSize: isMobile ? 24 : 28, fontWeight: 700,
                         fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em" }}>
            {isBu ? "$" + c.price.toFixed(4).replace(/0$/, "") : "$" + c.price.toFixed(2)}
          </span>
          {chgTxt ? (
            <span style={{ color: chg >= 0 ? UP : DOWN, fontWeight: 800, fontSize: 13.5 }}>
              {chg >= 0 ? "▲" : "▼"} {chgTxt}
            </span>
          ) : null}
        </div>
      ) : <div style={{ color: MUTED, marginTop: 6 }}>—</div>}
      {m.spark ? (
        <div style={{ marginTop: 8 }}>
          <Sparkline points={m.spark.points} />
          <div style={{ display: "flex", justifyContent: "space-between", color: MUTED, fontSize: 11.5, marginTop: 4 }}>
            <span>{m.spark.sessions} sessions</span>
            <span style={{ fontWeight: 700, color: m.spark.change >= 0 ? UP : DOWN }}>
              {isBu ? (m.spark.change >= 0 ? "+" : "−") + Math.abs(Math.round(m.spark.change * 100)) + "¢"
                    : (m.spark.change >= 0 ? "+" : "−") + "$" + Math.abs(m.spark.change).toFixed(2)}
            </span>
          </div>
        </div>
      ) : (
        <div style={{ color: MUTED, fontSize: 11.5, marginTop: 2 }}>
          {m.unit}{m.nearby ? " · nearby" : ""}
        </div>
      )}
      {/* NO CARRY / INVERSE BADGE. Kevin, 2026-09-01: "Get rid of carry and
          inverse badges, those are unneccesary." The spread is still MEASURED
          and it still reaches the reader — the briefing's grain and cattle facts
          say "the front spread is carry, Sep 26 → Dec 26 (+15¢)" in a sentence
          carrying the legs AND the number, which is what makes it checkable. The
          badge repeated the word without either. `markets.board[].shape` stays on
          the wire for those facts; do not re-add a badge here. */}
      {/* The percentile is descriptive — where today sits in its own history.
          No verdict, no badge: it ranks the present against the past and
          predicts nothing, which is exactly why it is safe to show. */}
      {p && p.pctile ? (
        <div style={{ marginTop: 8 }}>
          <div style={{ height: 5, background: "#e6eee6", borderRadius: 4, overflow: "hidden" }}>
            <div style={{ width: p.pctile.pctile + "%", height: "100%", background: "#5c9e5c" }} />
          </div>
          <div style={{ color: MUTED, fontSize: 10, marginTop: 3 }}>
            {p.pctile.pctile}th pctile · front month · {p.pctileYears}yr · n={p.pctile.n.toLocaleString()}
          </div>
        </div>
      ) : null}
    </button>
  );
}


// ── MARKETS IN PERSPECTIVE ────────────────────────────────────────────────
// Where was THIS CONTRACT on THIS DATE, in each of the last eleven years.
//
// Contract-specific, from futures_contract_history — Dec corn is CZ16..CZ26,
// not "whatever was front month". That distinction is the whole card: on
// 2026-08-21 the front month was Sep 26 at $4.8375 while Dec 26, the contract
// new-crop bushels are sold against, settled $5.0850.
//
// The stats panel sits BESIDE the chart rather than under it because the chart
// was occupying about half the card's width with the rest empty, and because
// "3rd highest of 11" is the sentence a reader takes away — it deserves to be
// read without counting bars.
export function PerspectiveCard({ h, onMonth, isMobile = false, monthDayLabel = "" }) {
  const { card, h2, eyebrow } = homeTheme(isMobile);
  if (!h) return null;
  // ── TWO KINDS OF SERIES ON ONE CARD, AND THE CARD SAYS WHICH ────────────────
  // Kevin, 2026-10-09: *"Can you do this chart for Milk class III and Class IV? See where it
  // compares to this day in history."*
  //
  // ⚠ THE GRAIN TABS AND THE MILK TABS ANSWER DIFFERENT QUESTIONS, and rendering them in the
  // same words would be the mixed-population error this repo has paid for repeatedly. Grain
  // is CONTRACT-SPECIFIC and DAILY — "where was December corn on October 8 in each of the
  // last eleven years" — and every bar names the contract live on that date. Milk has no
  // futures in this database at all (probed: no milk in futures_contract_history or
  // futures_settles_daily; drp_board reaches 1.7 years against the eleven this card uses), so
  // the dairy version is AMS's ANNOUNCED monthly Class III / IV — what a dairy was actually
  // PAID, which is arguably the better number, but it is a MONTHLY SETTLEMENT and not a quote.
  //
  // ⚠⚠ AND THE CURRENT MONTH IS NOT ANNOUNCED UNTIL THE NEXT ONE, so "today" on a milk tab is
  // the newest ANNOUNCED month — September, read in October. Labelling that "today" beside
  // tabs showing live boards would be a wrong fact, not a loose one. `seriesKind` carries the
  // distinction from the loader rather than leaving it to prose.
  const announced = h.seriesKind === "announced_month";
  const unavailable = !h.available;
  const W = isMobile ? 320 : 640, H = isMobile ? 190 : 230;
  const padL = 8, padB = 26, padT = 22;
  let body;
  if (unavailable) {
    body = (
      <div style={{ padding: "26px 4px", color: MUTED, fontSize: 12.5, lineHeight: 1.6 }}>
        <strong style={{ color: INK }}>Not enough history on file.</strong>
        <div style={{ marginTop: 4 }}>{h.reason}</div>
      </div>
    );
  } else {
    const vals = h.series.map((x) => x.close);
    const lo = Math.min(...vals, h.avg), hi = Math.max(...vals, h.avg);
    const span = (hi - lo) || 1;
    const y = (v) => padT + (H - padT - padB) * (1 - (v - lo + span * 0.14) / (span * 1.28));
    const bw = (W - padL - 6) / h.series.length;
    const avgY = y(h.avg);
    body = (
      <div style={{ overflowX: "auto" }}>
        <svg width={W} height={H} role="img"
             aria-label={announced
               ? (h.monthLabel || "") + " " + h.label + ", by year"
               : h.contractLabel + " " + h.label + " on this date, by year"}>
          <line x1={padL} x2={W - 4} y1={avgY} y2={avgY} stroke="#9bb09a" strokeDasharray="4 3" />
          {/* the dashed line SAYS what it is. A dotted line whose meaning the
              reader has to infer is decoration, not information. */}
          <text x={padL + 2} y={avgY - 5} fontSize="10" fill="#6d876c">
            {h.of}-yr avg ${h.avg.toFixed(2)}
          </text>
          {h.series.map((sx, i) => {
            const x = padL + i * bw + 3;
            const isCur = i === h.series.length - 1;
            const top = y(sx.close);
            const bwid = Math.max(7, bw - 7);
            return (
              <g key={sx.year}>
                <rect x={x} y={top} width={bwid} height={Math.max(1, H - padB - top)} rx={2}
                      fill={isCur ? "#2d6a2d" : (sx.close >= h.avg ? "#8fbc8f" : "#c3d6c3")} />
                <text x={x + bwid / 2} y={top - 5} fontSize="9.5" textAnchor="middle"
                      fill={isCur ? INK : "#6d876c"} fontWeight={isCur ? 700 : 400}>
                  {sx.close.toFixed(2)}
                </text>
                <text x={x + bwid / 2} y={H - 12} fontSize="9.5" textAnchor="middle"
                      fill={isCur ? INK : MUTED} fontWeight={isCur ? 700 : 400}>
                  {String(sx.year).slice(2)}
                </text>
                {/* The contract behind each bar, so the series can be checked. An ANNOUNCED
                    month has no contract — printing `undefined` under every bar, or a year
                    that merely repeats the axis label, would be noise dressed as provenance. */}
                {announced ? null : (
                  <text x={x + bwid / 2} y={H - 2} fontSize="7.5" textAnchor="middle" fill="#a8bda7">
                    {sx.contract}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
    );
  }

  const stat = (label, value, sub, tone) => (
    <div style={{ padding: "9px 0", borderBottom: "1px solid " + LINE }}>
      <div style={{ color: MUTED, fontSize: 9.5, letterSpacing: 0.8, textTransform: "uppercase", fontWeight: 700 }}>{label}</div>
      <div style={{ color: tone || INK, fontSize: 19, fontWeight: 700, fontVariantNumeric: "tabular-nums", lineHeight: 1.25 }}>{value}</div>
      {sub ? <div style={{ color: MUTED, fontSize: 10.5 }}>{sub}</div> : null}
    </div>
  );
  const money2 = (v) => "$" + Number(v).toFixed(2);
  const signed = (v) => (v >= 0 ? "+" : "−") + "$" + Math.abs(Number(v)).toFixed(2);

  return (
    <div style={{ ...card, marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
        <div>
          <div style={eyebrow}>Markets in perspective</div>
          <h2 style={{ ...h2, marginBottom: 2 }}>
            {announced
              ? (h.monthLabel || "") + " " + h.label.replace(/^Milk /, "") + ", by year"
              : h.contractLabel + " " + h.label.toLowerCase().replace(" (cbot)", "") + " on this date, by year"}
          </h2>
          <div style={{ color: MUTED, fontSize: 11.5 }}>
            {/* An ANNOUNCED series names its own nature and its own lag. "Announced
                September, read in October" is a fact a reader needs in order to compare this
                tab with the live boards beside it; leaving it to a small date would be
                exactly the kind of quiet mixed comparison this card exists to avoid. */}
            {announced ? (h.available
              ? "AMS announced Class price for " + h.monthLabel + ", each year from " +
                h.series[0].year + " to " + h.announcedYear + "." +
                (h.staleMonths > 0
                  ? " This is a monthly settlement, not a live quote — " + h.monthLabel +
                    " " + h.announcedYear + " is the newest month announced, " +
                    h.staleMonths + " month" + (h.staleMonths === 1 ? "" : "s") + " back."
                  : "")
              : "Announced monthly Class price.") : null}
            {/* ── SAY WHAT THE AXIS SAYS ────────────────────────────────────
                The bars are labelled by OBSERVATION year with the contract
                underneath, and for a delivery month that has already passed
                the two differ by one: in August 2016 the live July contract
                is CN17, because July 2016 expired that summer. Naming only
                the contracts — "CN17 through CN27" — beside an axis starting
                at 16 reads as an off-by-one error. It is not one; it is the
                rule working, and the caption has to say so rather than leave
                the reader to reconcile it. */}
            {announced ? null : h.available
              ? "Each " + monthDayLabel + " from " + h.series[0].year + " to " + h.today.year +
                ", against the " + h.contractLabel + " contract live on that date — " +
                h.series[0].contract + " through " + h.today.contract + "." +
                (h.series[0].contract.slice(-2) !== String(h.series[0].year).slice(-2)
                  ? " " + h.contractLabel + " expires before " + monthDayLabel +
                    ", so each year points at the following year's contract."
                  : "")
              : "Contract-specific series."}
            {!announced && h.newCrop ? " This is the new-crop contract." : ""}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {(h.months || []).map((m) => (
            <button key={m.month} onClick={() => onMonth(h.key, m.month)}
              style={{ background: m.month === h.contractMonth ? MID : "#f7faf7",
                       color: m.month === h.contractMonth ? "#fff" : MID,
                       border: "1px solid " + (m.month === h.contractMonth ? MID : LINE),
                       borderRadius: 20, padding: "4px 11px", cursor: "pointer",
                       fontSize: 11.5, fontWeight: 600 }}>
              {m.label.slice(0, 3)}{m.newCrop ? " ●" : ""}
            </button>
          ))}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 190px",
                    gap: isMobile ? 8 : 22, marginTop: 10, alignItems: "start" }}>
        {body}
        {h.available ? (
          <div>
            {/* An announced month has no contract and no trading DATE — its own month and
                 year ARE the identity, and fmtDate on a "YYYY-MM" string would print a day
                 that does not exist. */}
            {stat(announced ? "Latest announced" : "Today", money2(h.today.close),
                  announced ? (h.monthLabel + " " + h.announcedYear + " · announced")
                            : (h.today.contract + " · " + fmtDate(h.today.date)))}
            {stat(h.of + "-yr avg", money2(h.avg),
                  announced ? "same month, each year" : "same date, same contract month")}
            {stat("Vs average", signed(h.vsAvg) + "  " + (h.vsAvgPct >= 0 ? "+" : "") + h.vsAvgPct + "%",
                  null, h.vsAvg >= 0 ? UP : DOWN)}
            {/* The prior year is identified by its CONTRACT on a futures series and by its own
                month on an announced one, which carries no contract — naming one printed the
                literal word "undefined" under the figure. Found by RENDERING this card against
                real data, not by reading it; the arithmetic was right both times. */}
            {h.lastYear ? stat("Vs " + h.lastYear.year, signed(h.vsLastYear),
                  (announced ? h.monthLabel + " " + h.lastYear.year : h.lastYear.contract)
                    + " " + money2(h.lastYear.close),
                  h.vsLastYear >= 0 ? UP : DOWN) : null}
            <div style={{ padding: "9px 0" }}>
              <div style={{ color: MUTED, fontSize: 9.5, letterSpacing: 0.8, textTransform: "uppercase", fontWeight: 700 }}>Rank</div>
              <div style={{ color: INK, fontSize: 19, fontWeight: 700 }}>
                {h.rank}{["th","st","nd","rd"][(h.rank % 100 - 20) % 10] || ["th","st","nd","rd"][h.rank % 100] || "th"} highest
              </div>
              <div style={{ color: MUTED, fontSize: 10.5 }}>of {h.of} years · range {money2(h.lo)}–{money2(h.hi)}</div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}


// -- SECTION WRAPPERS - what each page actually mounts ------------------------

// ── WHAT YOU NEED TO KNOW — HEADLINES ONLY (2026-10-20) ───────────────────
//
// Kevin: "Make the home page 'what you need to know' section just news headlines
// with a link to more on the markets tab. Make some continuity there. Get rid of
// the fixed line 'Farm diesel is expensive'" — and "remove the generic section
// on home page about drought".
//
// So this section renders `data.headlines` and NOTHING ELSE: a headline, the
// outlet, its age, and a link to the article. The ranked FACT briefing
// (`briefing` / `briefingAlso` / `briefingMore`) is still on the wire and
// this component does not read it — which is what removed the farm-diesel line
// and the "Western NY drought severity (DSCI)" line, both of which were fact
// rows in that briefing. check-home-safe and check-home-headlines assert the
// fields stay unread, so the fact rows cannot come back through this card.
//
// CONTINUITY: api/_home.js fills `headlines` from the SAME loadPanel, queries
// and limit as the Markets news panels, taking the first rows of each. So a
// headline here is one of the first headlines on the Markets page the link
// below opens.
//
// `onMore` is optional and the link renders ONLY when it is a function. The
// caller passes it only where the viewer's nav actually offers Markets, so the
// link can never be a dead click (the quick-access rule).
function headlineAge(d) {
  if (d == null || !Number.isFinite(Number(d))) return null;
  const h = Number(d) * 24;
  if (h < 1) return "just now";
  if (h < 24) return Math.round(h) + "h ago";
  const days = Math.round(Number(d));
  return days + (days === 1 ? " day ago" : " days ago");
}

export function HomeBriefing({ data, isMobile = false, onMore = null }) {
  const { card, h2, eyebrow } = homeTheme(isMobile);
  const more = typeof onMore === "function" ? onMore : null;
  const groups = ((data && data.headlines) || []).filter((g) => g && (g.items || []).length);
  const sub = { color: MUTED, fontWeight: 700, fontSize: 10, letterSpacing: 0.7,
                textTransform: "uppercase", marginTop: 12, marginBottom: 2 };
  return (
    <div style={card}>
      <div style={eyebrow}>Market news</div>
      <h2 style={{ ...h2, marginBottom: 4 }}>What you need to know today</h2>
      <div style={{ color: MUTED, fontSize: 11.5, marginBottom: 4 }}>
        The latest headlines from the Markets news pages, newest first.
      </div>

      {groups.length ? groups.map((g) => (
        <div key={g.id}>
          <div style={sub}>{g.label}</div>
          {g.items.map((it, i) => {
            const age = headlineAge(it.ageDays);
            return (
              <div key={i} style={{ padding: "8px 0", borderBottom: "1px solid " + LINE }}>
                <a href={it.link} target="_blank" rel="noreferrer"
                   style={{ color: INK, fontSize: isMobile ? 13.5 : 14.5, lineHeight: 1.4, fontWeight: 600,
                            textDecoration: "none" }}>
                  {it.title}
                </a>
                <div style={{ color: MUTED, fontSize: 11, marginTop: 2 }}>
                  {it.source || "Unknown outlet"}{age ? " · " + age : ""}
                  {it.backfilled ? " · older item, shown because the week was quiet" : ""}
                </div>
              </div>
            );
          })}
        </div>
      )) : (
        <div style={{ color: MUTED, padding: "14px 0", fontSize: 13, lineHeight: 1.6 }}>
          No headlines could be loaded just now.
        </div>
      )}

      {more ? (
        <button onClick={() => more()}
          style={{ marginTop: 10, background: "none", border: "none", padding: 0, cursor: "pointer",
                   color: "#3d7a3d", fontSize: 12, fontWeight: 700 }}>
          More market news →
        </button>
      ) : null}
    </div>
  );
}

// parts: "all" (default) | "tiles" (the price row only) | "side" (milk + fed cash only).
// The pages place the two halves separately; the default renders both, as before.
export function HomeBoard({ data, isMobile = false, onNav = null, parts = "all" }) {
  const nav = typeof onNav === "function" ? onNav : null;
  const { card, h2, eyebrow } = homeTheme(isMobile);
  const board = (data && data.markets && data.markets.board) || [];
  const persp = (data && data.perspective) || {};
  const milk = data && data.milk;
  const cattle = data && data.cattle;
  const hasSide = !!((milk && milk.byClass) || (cattle && cattle.price != null));
  return (
    <>
    {/* ── markets: the price cards are the row the page opens on ── */}
    {parts !== "side" ? (
    <section aria-label="Where the board closed">
      <h2 style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>
        Where the board closed
      </h2>
      <div style={{ display: "grid", gap: isMobile ? 10 : 14,
                    gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fit, minmax(190px, 1fr))" }}>
        {board.map((m) => <Tile key={m.key} m={m} persp={persp} onNav={nav} isMobile={isMobile} />)}
      </div>
    </section>
    ) : null}
    {hasSide && parts !== "tiles" ? (
    <div style={{ ...card, marginTop: parts === "side" ? 0 : 16 }}>
      <div style={eyebrow}>Dairy &amp; cattle</div>
      <h2 style={{ ...h2, marginBottom: 4 }}>Milk floors and fed cash</h2>
      {milk && milk.byClass ? (
        <div style={{ marginTop: 10 }}>
          <div style={{ ...eyebrow, marginBottom: 6 }}>
            Milk · DRP board · {milk.state} · {Math.round(milk.coverage * 100)}% · {fmtDate(milk.asOf)}
          </div>
          {Object.keys(milk.byClass).map((cls) => (
            <div key={cls} style={{ marginBottom: 6 }}>
              <div style={{ color: MID, fontSize: 11.5, fontWeight: 700, marginBottom: 2 }}>{cls}</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {milk.byClass[cls].map((q) => (
                  <button key={q.quarter} onClick={nav ? () => nav("quoters") : undefined}
                    style={{ background: "#f7faf7", border: "1px solid " + LINE, borderRadius: 8,
                             padding: "5px 8px", cursor: nav ? "pointer" : "default", textAlign: "left" }}>
                    <div style={{ color: MUTED, fontSize: 9.5 }}>{q.quarter}</div>
                    <div style={{ color: INK, fontWeight: 700, fontSize: 13,
                                  fontVariantNumeric: "tabular-nums" }}>${q.price.toFixed(2)}</div>
                    <div style={{ color: MUTED, fontSize: 9.5 }}>net ${q.netFloor.toFixed(2)}</div>
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div style={{ color: MUTED, fontSize: 10.5, marginTop: 4 }}>
            Expected prices and net floors from RMA’s actuarial files — the same figures the DRP Quoter runs on.
          </div>
        </div>
      ) : null}
      {/* FED CASH, beside the futures tiles. The board says what the contract is
          worth; this says what cattle actually sold for last week, and the two
          are different questions. The slice is printed because five_area_weekly
          carries four classes x four selling bases x five grade tiers and an
          unlabelled average out of that table is not checkable. */}
      {cattle && cattle.price != null ? (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid " + LINE }}>
          <div style={{ ...eyebrow, marginBottom: 6 }}>
            Cattle · 5-Area fed cash · {fmtDate(cattle.asOf)}
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            <span style={{ color: INK, fontSize: 21, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
              ${cattle.price.toFixed(2)}
            </span>
            <span style={{ color: MUTED, fontSize: 11 }}>/cwt</span>
            {cattle.change != null ? (
              <span style={{ color: cattle.change >= 0 ? UP : DOWN, fontWeight: 700, fontSize: 12.5 }}>
                {cattle.change >= 0 ? "▲" : "▼"} {(cattle.change >= 0 ? "+" : "−") + "$" + Math.abs(cattle.change).toFixed(2)}
                <span style={{ color: MUTED, fontWeight: 400 }}> vs {fmtDate(cattle.prevDate)}</span>
              </span>
            ) : null}
          </div>
          <div style={{ color: MUTED, fontSize: 10.5, marginTop: 3 }}>{cattle.slice}</div>
        </div>
      ) : null}
    </div>
    ) : null}
    </>
  );
}

export function HomePerspective({ data, isMobile = false, months = {}, setMonths = () => {},
                                  perspKey = null, setPerspKey = () => {}, now = null }) {
  const persp = (data && data.perspective) || {};
  const perspKeys = Object.keys(persp);
  const activePersp = persp[perspKey] || persp[perspKeys[0]] || null;
  const monthDayLabel = monthDayLabelOf(now);
  if (!activePersp) return null;
  return (
    <>
      {/* THE COMMODITY PICKER SITS ABOVE THE CHART, NOT UNDER IT.
          Corn, soybeans and wheat have all been on this card since it shipped,
          but the selector was a row of small pills BELOW the chart, so the card
          read as a corn card with some footnotes and the soybean and wheat
          series went unnoticed. A control that selects what the chart is ABOUT
          belongs where the reader looks first. */}
      {perspKeys.length > 1 ? (
        <div style={{ display: "flex", gap: 6, marginTop: 16, flexWrap: "wrap" }}>
          {perspKeys.map((k) => {
            const on = !!(activePersp && activePersp.key === k);
            return (
              <button key={k} onClick={() => setPerspKey(k)}
                style={{ background: on ? MID : "#f7faf7", color: on ? "#fff" : MID,
                         border: "1px solid " + (on ? MID : LINE), borderRadius: 20,
                         padding: "5px 15px", cursor: "pointer", fontSize: 12.5,
                         fontWeight: on ? 700 : 600 }}>
                {persp[k].label}
              </button>
            );
          })}
        </div>
      ) : null}
      <PerspectiveCard h={activePersp} isMobile={isMobile} monthDayLabel={monthDayLabel}
        onMonth={(k, m) => setMonths((prev) => ({ ...prev, [k]: m }))} />
    </>
  );
}

// ── YOUR PROGRAM, PER ACRE ──────────────────────────────────────────────────
//
// "Potash is $492/ton" is a market. "Your potash costs 9.2 bu/ac of Dec 26 corn"
// is the number a grower budgets against, and it is one multiplication away —
// the card already knows what ONE unit of each input costs in bushels, and the
// only missing figure is how much of it goes on an acre.
//
// THAT FIGURE IS TYPED AND NEVER DEFAULTED. No table here can source a rate per
// acre, so a blank input is left OUT of the total and NAMED rather than filled
// with a plausible number — the same rule `grainBushelsFromCoverage` follows
// when a coverage line cannot be priced, and the same one the wheat quoter now
// follows on planted acres. A default here is a cost nobody entered printed as
// if they had.
//
// KEPT IN THE BROWSER, DELIBERATELY. Home's contract is that it reads no farmer
// data (`contract.readsFarmerData`, asserted by check-home-safe), and these
// cards render in the customer portal on exactly that basis. A rate is a
// per-viewer convenience, so localStorage is the honest store: it never reaches
// the server, never reaches another viewer, and cannot put this card on the
// wrong side of that contract. The cost of that choice is real and worth
// stating — the rates do NOT follow a user between devices or browsers. Making
// them do so is a table, an RLS policy and a decision about whose numbers they
// are; it is not a quiet upgrade to this file.
//
// THE ENTRY UNIT IS THE UNIT AMS PUBLISHES THE PRICE IN, so nothing has to be
// converted on the way in. Diesel and propane are quoted per GALLON, so the rate
// is gal/ac; every fertilizer is quoted per TON, so the rate is lb/ac over
// 2,000. UAN 32 is the row a grower will want to enter in gallons and it is
// deliberately lb/ac like the rest: turning gallons into pounds needs a density
// constant, this repo does not carry one for it, and a number that fits is not a
// source. It is two lines the day somebody produces one.
// ── WHOSE PROGRAM IS THIS? ──────────────────────────────────────────────────
//
// ⚠ localStorage IS PER-BROWSER, NOT PER-USER — AND THIS APP IMPERSONATES.
// Kevin, 2026-09-02: "dont let my inputs influence someone elses inputs."
//
// The first cut stored one program under one key for the whole origin, which is
// wrong in three live ways and only one of them needs two people at a desk:
//
//   * PREVIEW-AS. An admin previewing a farmer holds the FARMER's session and
//     the SAME browser store, so the farmer's Home would have rendered Kevin's
//     diesel and potash rates back at him under his own login. Entering and
//     leaving preview hard-reloads the page and drops `_sbCache` precisely so
//     that admin state cannot survive into an impersonated session —
//     localStorage survives that reload, so an unscoped key walks straight
//     through the one guard built to stop exactly this.
//   * A SHARED AGENCY MACHINE. Kevin and Mike on one computer are one origin.
//   * A FARMER ON A FAMILY COMPUTER, same shape from the customer side.
//
// So the key is scoped to the EFFECTIVE identity — `getCurrentUserId()`, the
// JWT `sub`, which is the previewed farmer while preview is on and the real
// session otherwise. That is the same "effective, not real" identity every
// other data decision in this app uses, and it is what makes preview honest
// here: previewing a farmer shows the FARMER's program (usually none), not the
// agent's.
//
// IT FAILS CLOSED. No identity means no store at all: the rates work for the
// session and are not written anywhere, and the card says so. Losing a few
// typed numbers is recoverable in thirty seconds; showing one person's cost
// program to another under their own name is not.
const PROGRAM_KEY_LEGACY = "silas.inputProgram.v1";   // unscoped — removed on sight
const PROGRAM_KEY_BASE = "silas.inputProgram.v2";

function programKey(identity) {
  const id = String(identity == null ? "" : identity).trim();
  return id ? PROGRAM_KEY_BASE + ":" + id : null;
}

// Every accessor is wrapped: a private window, cleared site data, a browser set
// to block storage and the artifact thumbnailer all THROW here rather than
// returning empty, and an unentered program must render, not blank the card.
function loadProgram(identity) {
  // THE LEGACY KEY IS DELETED, NEVER MIGRATED. It was written before the store
  // was scoped, so nothing records whose numbers are in it — and handing it to
  // whoever opens the page first is precisely the defect this scoping exists to
  // fix, just once instead of forever.
  try { window.localStorage.removeItem(PROGRAM_KEY_LEGACY); } catch (e) { /* storage unavailable */ }
  const k = programKey(identity);
  if (!k) return {};
  try {
    const raw = window.localStorage.getItem(k);
    const o = raw ? JSON.parse(raw) : null;
    return o && typeof o === "object" && !Array.isArray(o) ? o : {};
  } catch (e) { return {}; }
}
function saveProgram(identity, o) {
  const k = programKey(identity);
  if (!k) return;
  try { window.localStorage.setItem(k, JSON.stringify(o)); } catch (e) { /* storage unavailable */ }
}

// Has this identity actually entered a program? A stored object of zeros has not
// — it is the form's own default written back, and opening the editor on it
// would make an untouched card look half-filled.
function programHasEntry(o) {
  const keys = Object.keys(o || {});
  for (let i = 0; i < keys.length; i++) if (Number(o[keys[i]]) > 0) return true;
  return false;
}

const PROGRAM_RATE_UNIT = { gallon: "gal/ac", ton: "lb/ac" };

// How many PRICED units (gallons, or tons) one acre takes.
//
// ZERO CONTRIBUTES NOTHING AND IS NOT AN APPLIED INPUT. Every box now starts at
// 0 (Kevin, 2026-09-02: "default these to 0"), and that is safe for exactly one
// reason: **zero is the only value the form can put in a box the reader has not
// filled that changes no total.** A prefilled 150 lb would be a cost nobody
// entered, printed as though they had; a prefilled 0 adds nothing to the
// dollars, nothing to the bushels, and nothing to the "across N inputs" count,
// so an untouched row cannot inflate a program. That count is the honest signal
// that a program is partial, and it is on the headline rather than in a footnote
// nagging about rows the reader can see sitting at 0.
// ── HOW LONG IS THE COMPARISON WINDOW, IN WORDS ─────────────────────────────
//
// Kevin asked for a five-year average instead of two. The average has ALWAYS
// run over the whole stored series — it was the card's prose that said two
// years, and at 2.4 years (input_prices begins 2024-04-15) that was already
// wrong. The server now caps the window at five years and reports the span the
// series actually covers, so this label is MEASURED rather than typed: it
// reads "2.4-yr" today and becomes "5-yr" the day the history is long enough,
// with no second edit and without ever claiming a year that does not exist.
//
// ⚠ FALLS BACK TO A SPAN-FREE WORD, NEVER TO A NUMBER. An older payload has no
// windowYears; printing an assumed "5-yr" against it is exactly the invented
// history this change exists to avoid, so it says "historical" instead.
function spanLabelOf(power) {
  const yrs = (power || []).map((p) => p && p.windowYears).filter((y) => Number.isFinite(y) && y > 0);
  if (!yrs.length) return "historical";
  // The rows can differ (a product added later has less history). The SHORTEST
  // is what the weakest row supports, and a label must not overstate any row.
  const y = Math.min(...yrs);
  return (y >= 10 ? String(Math.round(y)) : String(Math.round(y * 10) / 10)) + "-yr";
}

function programUnitsPerAcre(r, raw) {
  const txt = String(raw == null ? "" : raw).trim();
  if (!txt) return null;
  const n = Number(txt);
  if (!Number.isFinite(n) || n <= 0) return null;
  return r.inputUnit === "ton" ? n / 2000 : n;
}

function InputProgram({ power = [], isMobile = false, identity = null }) {
  const spanLabel = spanLabelOf(power);
  const { h2, eyebrow } = homeTheme(isMobile);
  const [rates, setRates] = useState(function () { return loadProgram(identity); });
  const [open, setOpen] = useState(function () { return programHasEntry(loadProgram(identity)); });

  // The identity is decoded from the JWT and is available at first render, so
  // this normally never fires. It exists for the case where it does: a sign-out
  // and sign-in without a page reload must not leave the previous user's rates
  // on screen. Guarded on a ref so the ordinary path does not re-render twice.
  const lastIdentity = React.useRef(identity);
  useEffect(function () {
    if (lastIdentity.current === identity) return;
    lastIdentity.current = identity;
    const next = loadProgram(identity);
    setRates(next);
    setOpen(programHasEntry(next));
  }, [identity]);

  const rows = power.filter(function (r) { return !!PROGRAM_RATE_UNIT[r.inputUnit]; });
  if (!rows.length) return null;

  const stored = programKey(identity) != null;

  // A box the reader has never touched reads "0"; one they have cleared reads
  // empty until they leave it, so a rate can actually be retyped. `onBlur`
  // settles it back to 0 rather than to nothing.
  const rateValue = function (r) { return rates[r.id] == null ? "0" : rates[r.id]; };

  const setRate = function (id, v) {
    const next = Object.assign({}, rates);
    next[id] = String(v);
    setRates(next);
    saveProgram(identity, next);
  };
  const blurRate = function (id) {
    const cur = String(rates[id] == null ? "" : rates[id]).trim();
    const n = Number(cur);
    if (cur === "" || !Number.isFinite(n) || n < 0) setRate(id, "0");
  };
  const resetAll = function () {
    const zeroed = {};
    rows.forEach(function (r) { zeroed[r.id] = "0"; });
    setRates(zeroed);
    saveProgram(identity, zeroed);
  };

  const priced = [];
  rows.forEach(function (r) {
    // ⚠ THE TOTAL READS THE VALUE THE BOX SHOWS, via `rateValue` — not the raw
    // stored entry. Those were two reads of the same thing and they could
    // disagree: with the display defaulting through `rateValue` and the maths
    // reading `rates[r.id]` directly, changing the default to 150 would have put
    // 150 in the box and left it out of the total, so the form would display a
    // rate the figure above it ignored. Found by mutation, not by reading.
    const u = programUnitsPerAcre(r, rateValue(r));
    if (u == null) return;
    priced.push({
      r: r, units: u,
      dollars: u * r.inputPrice,
      // r.bushels is the SERVER's ratio (input price over the new-crop close).
      // Multiplying it keeps one definition of the ratio rather than re-deriving
      // it here from inputPrice/cropPrice, which is how two surfaces come to
      // disagree about the same input.
      bushels: u * r.bushels,
      avgBushels: r.avgBushels == null ? null : u * r.avgBushels,
    });
  });
  const buFor = function (r) {
    const hit = priced.filter(function (x) { return x.r.id === r.id; })[0];
    return hit ? hit.bushels : 0;
  };

  const totalD = priced.reduce(function (s, x) { return s + x.dollars; }, 0);
  const totalBu = priced.reduce(function (s, x) { return s + x.bushels; }, 0);
  // THE AVERAGE COMPARISON IS ALL-OR-NOTHING. Summing the rows that carry a
  // historical average and comparing that to a total built from all of them
  // subtracts a partial program from a full one, and the gap reads as a price
  // move. Shown only when every priced row can answer; otherwise named.
  const avgComplete = priced.length > 0 && priced.every(function (x) { return x.avgBushels != null; });
  const totalAvgBu = avgComplete ? priced.reduce(function (s, x) { return s + x.avgBushels; }, 0) : null;

  // Every ratio on this card divides by the same new-crop corn contract, so one
  // label is correct — but it is CHECKED rather than assumed, because a total
  // that silently mixed two contracts would be a spread wearing a headline.
  const months = priced.map(function (x) { return x.r.cropMonth; })
    .filter(function (v, i, a) { return v && a.indexOf(v) === i; });
  const cropLabel = months.length === 1 ? months[0] + " corn"
    : (rows[0] && rows[0].cropMonth ? rows[0].cropMonth + " corn" : "new-crop corn");

  // What the total actually mixes. The region and the report date move per input
  // (Maryland goes stale and the pick falls back), so a per-acre figure can be
  // built from two markets and two print dates — printed, never smoothed over.
  const regions = priced.map(function (x) { return x.r.region; })
    .filter(function (v, i, a) { return v && a.indexOf(v) === i; }).sort();
  const dates = priced.map(function (x) { return x.r.reportDate; })
    .filter(function (v, i, a) { return v && a.indexOf(v) === i; }).sort();

  const inputStyle = {
    width: 78, padding: "5px 7px", border: "1px solid " + LINE, borderRadius: 6,
    fontSize: 13, color: INK, background: "#fff", textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  };

  return (
    <div style={{ marginTop: 12, padding: isMobile ? "10px 12px" : "12px 14px",
                  background: "#f7faf7", border: "1px solid " + LINE, borderRadius: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline",
                    gap: 10, flexWrap: "wrap" }}>
        <div>
          <div style={eyebrow}>Your program, per acre</div>
          {priced.length ? (
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
              <span style={{ color: INK, fontSize: isMobile ? 20 : 24, fontWeight: 700,
                             fontVariantNumeric: "tabular-nums" }}>
                {totalBu.toFixed(1)} bu/ac
              </span>
              <span style={{ color: MID, fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
                ${totalD.toFixed(2)}/ac
              </span>
              <span style={{ color: MUTED, fontSize: 11.5 }}>
                of {cropLabel}, across {priced.length} input{priced.length === 1 ? "" : "s"}
              </span>
            </div>
          ) : (
            <div style={{ color: MUTED, fontSize: 12, lineHeight: 1.5, maxWidth: 560 }}>
              Enter what you put on an acre and the same benchmark prices above become
              a per-acre figure, in bushels of {cropLabel}. Every rate starts at 0, so
              nothing counts until you say it does.
            </div>
          )}
        </div>
        <button onClick={function () { setOpen(function (v) { return !v; }); }}
          style={{ background: "none", border: "none", padding: 0, cursor: "pointer",
                   color: "#3d7a3d", fontSize: 11.5, fontWeight: 600, whiteSpace: "nowrap" }}>
          {open ? "Hide rates" : priced.length ? "Edit rates" : "Enter your rates"}
        </button>
      </div>

      {/* OUTSIDE THE EDITOR, DELIBERATELY. This warning first sat in the fine
          print under the rate boxes, which are COLLAPSED until somebody enters
          something — so the one reader who needed it, the one about to type a
          program that will not be kept, could not see it until afterwards.
          A caveat behind a toggle is a caveat nobody reads. */}
      {!stored ? (
        <div style={{ color: "#8a6d3b", fontSize: 11, marginTop: 6, lineHeight: 1.5 }}>
          Rates are NOT being saved — this session could not identify who is signed in, and a
          program is never written to a store it cannot label. They will be gone when you
          leave the page.
        </div>
      ) : null}

      {/* ⚠ THE WINDOW IS MEASURED, NOT TYPED. Kevin asked for a five-year
          comparison; the average has always run over the whole stored series
          and it was this PROSE that said two years — already wrong at 2.4, and
          it would still have said two after five. The server caps the window at
          five years and reports what the series actually spans, so this reads
          "2.4-yr" now and "5-yr" the day there are five years, with no edit. */}
      {priced.length && totalAvgBu != null ? (
        <div style={{ color: MUTED, fontSize: 11.5, marginTop: 6, lineHeight: 1.5 }}>
          At each input's own {spanLabel} average price the same program is{" "}
          <strong style={{ color: MID }}>{totalAvgBu.toFixed(1)} bu/ac</strong>
          {" — a difference of "}
          <strong style={{ color: totalBu > totalAvgBu ? DOWN : UP }}>
            {(totalBu >= totalAvgBu ? "+" : "−") + Math.abs(totalBu - totalAvgBu).toFixed(1)} bu/ac
          </strong>
          {". The history begins " + (power[0] && power[0].windowFrom ? power[0].windowFrom : "2024") +
           ", so that is a " + spanLabel + " comparison and not a ten-year normal."}
        </div>
      ) : null}
      {priced.length && totalAvgBu == null ? (
        <div style={{ color: MUTED, fontSize: 11.5, marginTop: 6, lineHeight: 1.5 }}>
          No {spanLabel} comparison: at least one input you priced has too short a history to
          average, and summing only the rows that do would compare a partial program with a
          whole one.
        </div>
      ) : null}

      {open ? (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid " + LINE }}>
          <div style={{ display: "grid", gap: 6,
                        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
            {rows.map(function (r) {
              const bu = buFor(r);
              return (
                <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 8,
                                         padding: "3px 0", minWidth: 0 }}>
                  <label htmlFor={"prog-" + r.id}
                         style={{ color: INK, fontSize: 12, flex: 1, minWidth: 0,
                                  textTransform: "capitalize" }}>
                    {r.label}
                  </label>
                  <input id={"prog-" + r.id} type="number" inputMode="decimal" min="0" step="any"
                         value={rateValue(r)}
                         onFocus={function (e) { e.target.select(); }}
                         onChange={function (e) { setRate(r.id, e.target.value); }}
                         onBlur={function () { blurRate(r.id); }}
                         style={inputStyle} />
                  <span style={{ color: MUTED, fontSize: 10.5, width: 42 }}>
                    {PROGRAM_RATE_UNIT[r.inputUnit]}
                  </span>
                  <span style={{ color: bu > 0 ? MID : "#c3d2c1", fontSize: 11.5, width: 74,
                                 textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {bu.toFixed(2)} bu
                  </span>
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline",
                        gap: 10, flexWrap: "wrap", marginTop: 8 }}>
            <div style={{ color: MUTED, fontSize: 10.5, lineHeight: 1.5, flex: 1, minWidth: 220 }}>
              Fertilizer is entered as <strong>lb/ac of product</strong> because AMS prices it by
              the ton; fuels are gal/ac. UAN is lb/ac for the same reason — converting gallons
              needs a density figure this app holds no source for.{" "}
              {stored
                ? "Rates are saved in this browser under your login only: they are never sent anywhere, nobody else who uses this browser sees them, and they will not follow you to another device."
                : ""}
            </div>
            <button onClick={resetAll}
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer",
                       color: "#3d7a3d", fontSize: 11.5, fontWeight: 600, whiteSpace: "nowrap" }}>
              Reset to 0
            </button>
          </div>
        </div>
      ) : null}

      {/* WHAT THE TOTAL MIXES, NAMED. The region and report date move per input,
          so a per-acre figure can be built from two markets and two print dates.
          There is no longer a "not in this figure" line: every row starts at 0
          and sits visibly at 0 until it is changed, so an input outside the
          total is a statement on the form rather than an omission from it. */}
      {priced.length ? (
        <div style={{ color: MUTED, fontSize: 10.5, marginTop: 8, lineHeight: 1.5,
                      borderTop: "1px solid " + LINE, paddingTop: 8 }}>
          {"Priced off " + regions.join(" and ") + " " +
           (dates.length === 1 ? "as of " + fmtDate(dates[0])
                               : "as of " + fmtDate(dates[0]) + "–" + fmtDate(dates[dates.length - 1])) +
           ", against " + cropLabel + ". Regional published benchmarks, not a quote for any farm."}
        </div>
      ) : null}
    </div>
  );
}

export function HomePurchasingPower({ data, isMobile = false, identity = null }) {
  const spanLabel = spanLabelOf((data && data.purchasingPower) || []);
  const { card, h2, eyebrow } = homeTheme(isMobile);
  const power = (data && data.purchasingPower) || [];
  const inputPressure = (data && data.inputPressure) || null;
  if (!power.length) return null;
  return (
    <>
    {/* ── FARM PURCHASING POWER ────────────────────────────────────────────
        An input priced in the crop that pays for it. The whole table is here
        because the comparison between rows is the point — potash cheap while
        diesel is dear is a different picture from everything being dear — but
        only an entry near an edge of its own history is promoted into "what
        matters today".

        Two labels are load-bearing and neither is decoration: the REGION,
        because this is a published regional benchmark and never a quote for
        any farm, and the WINDOW, because the series begins 2024 and cannot
        support the ten-year normal a reader will assume. */}
    {power.length ? (
      <div style={{ ...card, marginTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
          <div>
            <div style={eyebrow}>Farm purchasing power</div>
            <h2 style={{ ...h2, marginBottom: 2 }}>
              What inputs cost, in bushels{power[0] && power[0].cropMonth ? " of " + power[0].cropMonth + " corn" : ""}
            </h2>
            <div style={{ color: MUTED, fontSize: 11.5 }}>
              USDA AMS regional benchmarks against the new-crop board · where each sits in its own {spanLabel} range.
              Published regional prices — not a quote for any farm.
            </div>
          </div>
        </div>
        {/* THE ANSWER, ABOVE THE TABLE THAT EVIDENCES IT. Seven percentile
            bars is a reference table; the question is "what is expensive and
            what is cheap", and making the reader derive that from the rows is
            the page failing to do its job. Each line carries the input-vs-crop
            decomposition, so a ratio extreme is never reported as an input
            story when the crop is what moved. */}
        {inputPressure && inputPressure.items.length ? (
          <div style={{ marginTop: 12, padding: "10px 12px", background: "#f7faf7",
                        border: "1px solid " + LINE, borderRadius: 8 }}>
            <div style={{ ...eyebrow, marginBottom: 6 }}>Input pressure</div>
            {inputPressure.items.map((i) => (
              <div key={i.kind + i.id} style={{ display: "flex", gap: 8, alignItems: "baseline",
                                                padding: "3px 0", fontSize: 12.5, lineHeight: 1.5 }}>
                <span style={{ minWidth: 84, color: MUTED, fontSize: 10.5, letterSpacing: 0.5,
                               textTransform: "uppercase", fontWeight: 700 }}>
                  {i.kind === "expensive" ? "Most expensive" : i.kind === "cheap" ? "Cheapest" : "Elevated"}
                </span>
                <span style={{ color: INK, flex: 1, minWidth: 0 }}>
                  <strong style={{ textTransform: "capitalize" }}>{i.label}</strong>
                  {" — "}{i.where}
                  {i.note ? <span style={{ color: MUTED }}>{". "}{i.note}</span> : null}
                </span>
              </div>
            ))}
          </div>
        ) : null}

        {/* THE PAYOFF, ABOVE THE EVIDENCE. The table says what one unit of
            each input costs; this says what an ACRE of the reader's own
            program costs, in the crop that pays for it. It sits here rather
            than under the table because a per-acre figure is the answer and
            the seven percentile rows are the working. */}
        <InputProgram power={power} isMobile={isMobile} identity={identity} />

        {/* ONE CARD PER INPUT, NOT A TABLE. The six-column table ran off the
            side of a phone, so "where it sits" — the column the card exists for —
            was the one a reader on a phone never saw. Each card keeps every fact
            the table carried (region, report date, print cadence, the per-nutrient
            cost) and leads with the two a reader acts on: bushels per unit, and
            where that sits in its own range. */}
        <div style={{ display: "grid", gap: 10, marginTop: 12,
                      gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(280px, 1fr))" }}>
          {power.map((r) => {
            const pct = r.pctile ? r.pctile.pctile : null;
            // AN EXTREME READS AS A WORD, NOT AS "0th". The minimum of a sample is
            // literally the 0th percentile, and "0th percentile of 58" reads like a
            // null rather than like the lowest value on record. Same at the top.
            // 1st, 2nd, 3rd, 11th, 21st — "3th pctile of 58" was on screen once.
            const ordN = (n) => {
              const sfx = ["th", "st", "nd", "rd"], v = n % 100;
              return n + (sfx[(v - 20) % 10] || sfx[v] || sfx[0]);
            };
            const pctText = pct == null ? null
              : pct <= 1 ? "lowest of " + r.pctile.n
              : pct >= 99 ? "highest of " + r.pctile.n
              : ordN(pct) + " percentile of " + r.pctile.n;
            const vsText = r.vsAvgPct == null ? null
              : (r.vsAvgPct >= 0 ? "+" : "") + r.vsAvgPct + "% vs its average";
            const bu = r.bushels >= 100 ? r.bushels.toFixed(0)
              : r.bushels >= 10 ? r.bushels.toFixed(1) : r.bushels.toFixed(2);
            const name = r.label.charAt(0).toUpperCase() + r.label.slice(1);
            // Keep the dot inside the track at both ends, so 0 and 100 are still visible.
            const at = pct == null ? null : Math.max(3, Math.min(97, pct));
            return (
              <div key={r.id} style={{ border: "1px solid " + LINE, borderRadius: 12, padding: "12px 14px",
                                       background: CARD }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                  <div style={{ color: INK, fontWeight: 700, fontSize: 14 }}>{name}</div>
                  <div style={{ color: INK, fontWeight: 700, fontSize: 14, whiteSpace: "nowrap",
                                fontVariantNumeric: "tabular-nums" }}>
                    {bu} bu/{r.inputUnit}
                  </div>
                </div>
                <div style={{ color: MUTED, fontSize: 12, marginTop: 2 }}>
                  ${Number(r.inputPrice).toFixed(2)} a {r.inputUnit} · {r.region}
                  {r.avgBushels == null ? "" : " · avg " + (r.avgBushels >= 100 ? r.avgBushels.toFixed(0)
                    : r.avgBushels >= 10 ? r.avgBushels.toFixed(1) : r.avgBushels.toFixed(2)) + " bu"}
                </div>
                <div style={{ color: MUTED, fontSize: 10.5, marginTop: 1 }}>
                  {fmtDate(r.reportDate)}
                  {/* ⚠ A DATE WITHOUT A CADENCE READS AS A STALLED FEED. Kevin, on a
                      card showing "Illinois · Sep 28" twelve days later: "why are
                      inputs from September 28th?" Nothing was wrong — AMS prints this
                      series every 14 days and Sep 28 was the newest one. Saying when
                      the next is due separates a current series from a dead one on
                      sight; Maryland, which stopped at Jun 22, reads overdue here. */}
                  {r.nextDue && !r.printOverdue ? " · next " + fmtDate(r.nextDue) : ""}
                  {r.printOverdue
                    ? <span style={{ color: "#a5461b" }}>{" · overdue — was due " + fmtDate(r.nextDue)}</span>
                    : ""}
                  {r.nutrient && r.nutrient.single
                    ? " · $" + r.nutrient.costPerLb + "/lb " + r.nutrient.nutrientLabel : ""}
                  {r.nutrient && !r.nutrient.single ? " · " + r.nutrient.grade : ""}
                </div>
                {pct == null ? (
                  <div style={{ color: MUTED, fontSize: 11.5, marginTop: 10 }}>
                    Window too thin to rank{vsText ? " — " + vsText : ""}
                  </div>
                ) : (
                  <>
                    <div style={{ position: "relative", height: 8, marginTop: 12, borderRadius: 6,
                                  background: "linear-gradient(90deg, #dbe8d3, #f2e4bf 55%, #ecc0b0)" }}>
                      <div style={{ position: "absolute", left: "calc(" + at + "% - 9px)", top: -5,
                                    width: 18, height: 18, borderRadius: 18, background: INK,
                                    border: "2.5px solid #fff", boxSizing: "border-box",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.25)" }} />
                    </div>
                    <div style={{ color: MID, fontSize: 12, fontWeight: 600, marginTop: 10 }}>
                      {pctText}{vsText ? " — " + vsText : ""}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ color: MUTED, fontSize: 10.5, marginTop: 8, lineHeight: 1.5 }}>
          Bushels of the new-crop contract that one unit of the input costs — higher is
          more expensive. A per-nutrient cost is shown only for single-nutrient products;
          a blended price cannot be split between nutrients without inventing the split.
          {power[0] && power[0].windowFrom
            ? " History begins " + power[0].windowFrom + ", so these are " + spanLabel +
              " comparisons, not ten-year normals."
            : ""}
        </div>
        {/* WHY THIS REGION, AND WHAT IT COST TO USE IT. A silent substitution
            moves the number without moving the label — the reader sees an
            unfamiliar market beside a familiar input and supplies their own
            explanation. Maryland is the closest published market to a New York
            book and leads whenever it is current; when it is not, the card says
            which market answered instead and how far apart the regions are. */}
        {(function () {
          const swapped = power.filter(function (r) { return r.nearRegionStale && r.nearRegion; });
          if (!swapped.length) return null;
          const r = swapped[0];
          const spread = power
            .map(function (x) { return x.regionSpread; })
            .filter(function (s) { return s && s.ratio > 1; })
            .sort(function (a, b) { return b.ratio - a.ratio; })[0];
          return (
            <div style={{ color: MUTED, fontSize: 10.5, marginTop: 6, lineHeight: 1.5,
                          borderTop: "1px solid " + LINE, paddingTop: 8 }}>
              <strong style={{ color: MID }}>Not the nearest market.</strong>{" "}
              {r.nearRegion} is the closest published benchmark to a New York book, but its
              last report is {fmtDate(r.nearRegionAsOf)} — too old to divide by today’s board,
              so {r.region} answers instead.
              {spread ? " On the latest print the published regions span " +
                        "$" + spread.lo.toFixed(2) + "–$" + spread.hi.toFixed(2) +
                        " for the same input (" + spread.ratio.toFixed(2) + "×), so the region is " +
                        "worth reading, not just the number." : ""}
            </div>
          );
        })()}
      </div>
    ) : null}
    </>
  );
}
